import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { getCachedUsers } from '../cache/appDataCache';

type ProgressPreset = 'today' | 'yesterday' | 'custom';

interface AdminOption {
  id: string;
  name: string;
  email: string;
}

interface UserProgressRow {
  userId: string;
  name: string;
  email: string;
  role: string;
  reached: number;
  notReached: number;
  updated: number;
  rejected: number;
  totalActivity: number;
}

interface ProgressResponse {
  timezone: string;
  range: {
    preset: ProgressPreset;
    from: string;
    to: string;
    start: string;
    end: string;
  };
  users: UserProgressRow[];
  totals: {
    reached: number;
    notReached: number;
    updated: number;
    rejected: number;
    totalActivity: number;
  };
}

function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const Settings: React.FC = () => {
  const [preset, setPreset] = useState<ProgressPreset>('today');
  const [fromDate, setFromDate] = useState(todayYmd());
  const [toDate, setToDate] = useState(todayYmd());
  const [adminId, setAdminId] = useState('all');
  const [admins, setAdmins] = useState<AdminOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<ProgressResponse | null>(null);

  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const users = await getCachedUsers();
        const adminList = users
          .filter((u: any) => u.role === 'ADMIN' && u.isActive !== false)
          .map((u: any) => ({
            id: u.id,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
            email: u.email
          }))
          .sort((a: AdminOption, b: AdminOption) => a.name.localeCompare(b.name));
        setAdmins(adminList);
      } catch (err) {
        console.error('Failed to load admins:', err);
      }
    };
    loadAdmins();
  }, []);

  const fetchProgress = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params: Record<string, string> = {
        preset,
        role: 'ADMIN'
      };
      if (preset === 'custom') {
        params.from = fromDate;
        params.to = toDate;
      }
      if (adminId !== 'all') {
        params.userId = adminId;
      }

      const response = await axios.get(`${API_BASE_URL}/progress/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('Failed to load admin progress');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load admin progress');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (preset !== 'custom') {
      fetchProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, adminId]);

  useEffect(() => {
    if (preset === 'custom') {
      fetchProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, adminId]);

  const totals = data?.totals;

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Admin progress — select an admin and date range to see Email Sent, Email Failed, and updates (IST).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Admin Progress
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 2
            }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Admin</InputLabel>
              <Select
                label="Admin"
                value={adminId}
                onChange={(e) => setAdminId(e.target.value)}
              >
                <MenuItem value="all">All admins</MenuItem>
                {admins.map((admin) => (
                  <MenuItem key={admin.id} value={admin.id}>
                    {admin.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ButtonGroup variant="outlined" size="small">
              <Button
                variant={preset === 'today' ? 'contained' : 'outlined'}
                onClick={() => setPreset('today')}
              >
                Today
              </Button>
              <Button
                variant={preset === 'yesterday' ? 'contained' : 'outlined'}
                onClick={() => setPreset('yesterday')}
              >
                Yesterday
              </Button>
              <Button
                variant={preset === 'custom' ? 'contained' : 'outlined'}
                onClick={() => setPreset('custom')}
              >
                Custom range
              </Button>
            </ButtonGroup>

            {preset === 'custom' && (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchProgress}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {data?.range && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Showing {data.range.from}
              {data.range.from !== data.range.to ? ` → ${data.range.to}` : ''} ({data.timezone})
              {adminId !== 'all' && admins.find((a) => a.id === adminId)
                ? ` · ${admins.find((a) => a.id === adminId)!.name}`
                : ' · All admins'}
            </Typography>
          )}

          {totals && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                label={`Email Sent: ${totals.reached}`}
                sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 600, '& .MuiChip-label': { color: '#fff' } }}
              />
              <Chip
                label={`Email Failed: ${totals.notReached}`}
                sx={{ bgcolor: '#ed6c02', color: '#fff', fontWeight: 600, '& .MuiChip-label': { color: '#fff' } }}
              />
              <Chip
                label={`Updated: ${totals.updated}`}
                sx={{ bgcolor: '#0288d1', color: '#fff', fontWeight: 600, '& .MuiChip-label': { color: '#fff' } }}
              />
              <Chip
                label={`Rejected: ${totals.rejected}`}
                sx={{ bgcolor: '#d32f2f', color: '#fff', fontWeight: 600, '& .MuiChip-label': { color: '#fff' } }}
              />
            </Box>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Admin name</TableCell>
                    <TableCell align="right">Email Sent</TableCell>
                    <TableCell align="right">Email Failed</TableCell>
                    <TableCell align="right">Updated</TableCell>
                    <TableCell align="right">Rejected</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.users || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No admin progress found for this filter.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (data?.users || []).map((row) => (
                      <TableRow key={row.userId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.email}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{row.reached}</TableCell>
                        <TableCell align="right">{row.notReached}</TableCell>
                        <TableCell align="right">{row.updated}</TableCell>
                        <TableCell align="right">{row.rejected}</TableCell>
                        <TableCell align="right">
                          <strong>{row.totalActivity}</strong>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings;
